/**
 * Drive Parameters Input Component
 *
 * Input fields for drive specifications with improved visual hierarchy
 * and horizontal packing for complementary inputs.
 */

import { Box, TextField, Grid, Divider, Accordion, AccordionSummary, AccordionDetails, Typography, Slider, ToggleButtonGroup, ToggleButton } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import type { DriveParameters } from '../../types';
import { TooltipIcon, ValidationIcon } from '../ui';
import { validateField } from '../../data/validation';
import { DriveSelector } from './DriveSelector';

interface DriveParametersProps {
  value: DriveParameters;
  onChange: (value: DriveParameters) => void;
}

export function DriveParametersInput({ value, onChange }: DriveParametersProps) {
  const handleChange = (field: keyof DriveParameters) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newValue = event.target.value;
    // Convert to number for numeric fields
    const numericFields = ['driveSizeTB', 'driveCost', 'driveIOPS', 'peakSequentialMBps', 'wattsIdle', 'wattsLoad', 'afr'];
    onChange({ ...value, [field]: numericFields.includes(field) ? Number(newValue) : newValue });
  };

  const handleAFRChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onChange({ ...value, afr: Number(event.target.value) });
  };

  // Validation for AFR
  const afrValidations = validateField('afr', value.afr);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* Drive Database Selection */}
      <DriveSelector value={value} onChange={onChange} />

      <Divider />

      {/* Custom Drive Parameters */}
      <Accordion defaultExpanded disableGutters elevation={0} sx={{ '&:before': { display: 'none' } }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 36, '& .MuiAccordionSummary-content': { margin: '8px 0' } }}>
          <Typography variant="caption" sx={{ fontWeight: 500, fontSize: '0.75rem' }}>
            Basic Parameters
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 1, px: 0 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {/* Drive Type Selection - Toggle Buttons */}
            <Box>
              <Typography variant="caption" sx={{ fontSize: '0.7rem', fontWeight: 500, display: 'block', mb: 0.5 }}>
                Drive Type
                <TooltipIcon tooltipKey="driveType" />
              </Typography>
              <ToggleButtonGroup
                value={value.driveType || 'enterprise'}
                exclusive
                onChange={(_, newVal) => newVal && onChange({ ...value, driveType: newVal as 'consumer' | 'enterprise' })}
                size="small"
                fullWidth
                sx={{ height: 28 }}
              >
                <ToggleButton value="consumer" sx={{ fontSize: '0.7rem', py: 0.5, px: 1 }}>
                  Consumer
                </ToggleButton>
                <ToggleButton value="enterprise" sx={{ fontSize: '0.7rem', py: 0.5, px: 1 }}>
                  Enterprise
                </ToggleButton>
              </ToggleButtonGroup>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', display: 'block', mt: 0.5 }}>
                {value.driveType === 'consumer' ? 'Desktop drives (1 in 10¹⁴ URE)' : 'NAS/Datacenter (1 in 10¹⁵ URE)'}
              </Typography>
            </Box>

            {/* Drive Size + Cost - Horizontal Layout */}
            <Grid container spacing={1}>
              <Grid item xs={6}>
                <Typography variant="caption" sx={{ fontSize: '0.7rem', display: 'block', mb: 0.5 }}>
                  Size (TB)
                </Typography>
                <TextField
                  type="number"
                  value={value.driveSizeTB}
                  onChange={handleChange('driveSizeTB')}
                  inputProps={{ min: 1, max: 30, step: 0.5, style: { fontSize: '0.8rem' } }}
                  fullWidth
                  size="small"
                  sx={{ '& .MuiInputBase-input': { py: 0.5 } }}
                />
              </Grid>

              <Grid item xs={6}>
                <Typography variant="caption" sx={{ fontSize: '0.7rem', display: 'block', mb: 0.5 }}>
                  Cost ($)
                </Typography>
                <TextField
                  type="number"
                  value={value.driveCost}
                  onChange={handleChange('driveCost')}
                  inputProps={{ min: 0, step: 10, style: { fontSize: '0.8rem' } }}
                  fullWidth
                  size="small"
                  sx={{ '& .MuiInputBase-input': { py: 0.5 } }}
                />
              </Grid>
            </Grid>
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* Advanced Power & Performance - Collapsed by default */}
      <Accordion disableGutters elevation={0} sx={{ '&:before': { display: 'none' } }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 36, '& .MuiAccordionSummary-content': { margin: '8px 0' } }}>
          <Typography variant="caption" sx={{ fontWeight: 500, fontSize: '0.75rem' }}>
            Power & Performance
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 1, px: 0 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {/* Power Consumption - Horizontal */}
            <Grid container spacing={1}>
              <Grid item xs={6}>
                <Typography variant="caption" sx={{ fontSize: '0.7rem', display: 'block', mb: 0.5 }}>
                  Idle (W)
                </Typography>
                <TextField
                  type="number"
                  value={value.wattsIdle}
                  onChange={(e) => onChange({ ...value, wattsIdle: Number(e.target.value) })}
                  inputProps={{ min: 0, step: 0.1, style: { fontSize: '0.8rem' } }}
                  fullWidth
                  size="small"
                  sx={{ '& .MuiInputBase-input': { py: 0.5 } }}
                />
              </Grid>

              <Grid item xs={6}>
                <Typography variant="caption" sx={{ fontSize: '0.7rem', display: 'block', mb: 0.5 }}>
                  Load (W)
                </Typography>
                <TextField
                  type="number"
                  value={value.wattsLoad}
                  onChange={(e) => onChange({ ...value, wattsLoad: Number(e.target.value) })}
                  inputProps={{ min: 0, step: 0.1, style: { fontSize: '0.8rem' } }}
                  fullWidth
                  size="small"
                  sx={{ '& .MuiInputBase-input': { py: 0.5 } }}
                />
              </Grid>
            </Grid>

            {/* IOPS + AFR/Peak Speed - Horizontal */}
            <Grid container spacing={1}>
              <Grid item xs={4}>
                <Typography variant="caption" sx={{ fontSize: '0.7rem', display: 'block', mb: 0.5 }}>
                  IOPS
                  <TooltipIcon tooltipKey="driveIOPS" />
                </Typography>
                <TextField
                  type="number"
                  value={value.driveIOPS}
                  onChange={(e) => onChange({ ...value, driveIOPS: Number(e.target.value) })}
                  inputProps={{ min: 1, step: 1, style: { fontSize: '0.8rem' } }}
                  fullWidth
                  size="small"
                  sx={{ '& .MuiInputBase-input': { py: 0.5 } }}
                />
              </Grid>

              {/* AFR Input - Only shown for new drives (driveAge = 0) */}
              {(value.driveAge ?? 0) === 0 ? (
                <Grid item xs={4}>
                  <Typography variant="caption" sx={{ fontSize: '0.7rem', display: 'block', mb: 0.5 }}>
                    AFR (%)
                    <TooltipIcon tooltipKey="AFR" />
                    <ValidationIcon validations={afrValidations} />
                  </Typography>
                  <TextField
                    type="number"
                    value={value.afr}
                    onChange={handleAFRChange}
                    inputProps={{ min: 0, max: 10, step: 0.1, style: { fontSize: '0.8rem' } }}
                    error={afrValidations.some(v => v.severity === 'warning')}
                    fullWidth
                    size="small"
                    sx={{ '& .MuiInputBase-input': { py: 0.5 } }}
                  />
                </Grid>
              ) : (
                <Grid item xs={4}>
                  <Typography variant="caption" sx={{ fontSize: '0.7rem', display: 'block', mb: 0.5 }}>
                    Eff. AFR (%)
                    <TooltipIcon tooltipKey="AFR" />
                  </Typography>
                  <TextField
                    type="number"
                    value={(() => {
                      const age = value.driveAge ?? 0;
                      const multiplier = age <= 1 ? 1.3 :
                                        age <= 3 ? 1.0 :
                                        age <= 6 ? 1.1 :
                                        age <= 8 ? 1.8 :
                                        age <= 10 ? 3.0 : 4.25;
                      return (value.afr * multiplier).toFixed(2);
                    })()}
                    InputProps={{
                      readOnly: true,
                    }}
                    fullWidth
                    size="small"
                    sx={{ '& .MuiInputBase-input': { py: 0.5, fontSize: '0.8rem' } }}
                  />
                </Grid>
              )}

              <Grid item xs={4}>
                <Typography variant="caption" sx={{ fontSize: '0.7rem', display: 'block', mb: 0.5 }}>
                  Peak (MB/s)
                  <TooltipIcon tooltipKey="peakSequentialSpeed" />
                </Typography>
                <TextField
                  type="number"
                  value={value.peakSequentialMBps}
                  onChange={(e) => onChange({ ...value, peakSequentialMBps: Number(e.target.value) })}
                  inputProps={{ min: 1, step: 1, style: { fontSize: '0.8rem' } }}
                  fullWidth
                  size="small"
                  sx={{ '& .MuiInputBase-input': { py: 0.5 } }}
                />
              </Grid>
            </Grid>
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* Drive Age (for Homelab Users with Used Drives) */}
      <Accordion disableGutters elevation={0} sx={{ '&:before': { display: 'none' } }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 36, '& .MuiAccordionSummary-content': { margin: '8px 0' } }}>
          <Typography variant="caption" sx={{ fontWeight: 500, fontSize: '0.75rem' }}>
            Drive Age (Used Drives)
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 1, px: 0 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Box>
              <Typography variant="caption" sx={{ fontSize: '0.7rem', display: 'block', mb: 0.5 }}>
                Age: <strong>{value.driveAge ?? 0} year{value.driveAge !== 1 ? 's' : ''}</strong>
                <TooltipIcon tooltipKey="driveAge" />
              </Typography>
              <Slider
                value={value.driveAge ?? 0}
                onChange={(_, newValue) => onChange({ ...value, driveAge: newValue as number })}
                size="small"
                min={0}
                max={15}
                marks={[
                  { value: 0, label: '0' },
                  { value: 5, label: '5' },
                  { value: 10, label: '10' },
                  { value: 15, label: '15' }
                ]}
                valueLabelDisplay="auto"
                sx={{
                  color: '#2196f3',
                  height: 4,
                  '& .MuiSlider-thumb': { width: 12, height: 12 },
                }}
              />
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', display: 'block', mt: 0.5 }}>
                AFR auto-calculated from Backblaze data
              </Typography>
            </Box>

            {(value.driveAge ?? 0) > 0 && (
              <Box sx={{ p: 1, bgcolor: 'info.main', color: 'info.contrastText', borderRadius: 1 }}>
                <Typography variant="caption" sx={{ fontWeight: 500, fontSize: '0.7rem' }}>
                  ✓ AFR calculated from Backblaze {value.driveAge}-year data
                </Typography>
              </Box>
            )}
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* Advanced MTTP Settings */}
      <Accordion disableGutters elevation={0} sx={{ '&:before': { display: 'none' } }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 36, '& .MuiAccordionSummary-content': { margin: '8px 0' } }}>
          <Typography variant="caption" sx={{ fontWeight: 500, fontSize: '0.75rem' }}>
            Advanced MTTP Settings
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 1, px: 0 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ToggleButtonGroup
                value={value.useWeibullDistribution || false}
                exclusive
                onChange={(_, newVal) => onChange({ ...value, useWeibullDistribution: newVal ?? false })}
                size="small"
              >
                <ToggleButton value={true} sx={{ fontSize: '0.7rem', py: 0.5, px: 1 }}>
                  Use Weibull
                </ToggleButton>
                <ToggleButton value={false} sx={{ fontSize: '0.7rem', py: 0.5, px: 1 }}>
                  Exponential
                </ToggleButton>
              </ToggleButtonGroup>
              <TooltipIcon tooltipKey="useWeibullDistribution" />
            </Box>

            {value.useWeibullDistribution && (
              <Box>
                <Typography variant="caption" sx={{ fontSize: '0.7rem', display: 'block', mb: 0.5 }}>
                  Shape (β): <strong>{value.weibullShapeParameter?.toFixed(1) || '1.0'}</strong>
                  <TooltipIcon tooltipKey="weibullShapeParameter" />
                </Typography>
                <Slider
                  value={value.weibullShapeParameter || 1.0}
                  onChange={(_, newValue) => onChange({ ...value, weibullShapeParameter: newValue as number })}
                  size="small"
                  min={0.5}
                  max={3.0}
                  step={0.1}
                  marks={[
                    { value: 1.0, label: '1.0' },
                    { value: 2.0, label: '2.0' },
                    { value: 3.0, label: '3.0' }
                  ]}
                  valueLabelDisplay="auto"
                  sx={{
                    color: '#ff5722',
                    height: 4,
                    '& .MuiSlider-thumb': { width: 12, height: 12 },
                  }}
                />
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', display: 'block', mt: 0.5 }}>
                  β=1.0 constant, β&gt;1 wear-out, β&lt;1 infant mortality
                </Typography>
              </Box>
            )}
          </Box>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
}
